/**
 * VolumeCheckOverlay Component
 * 
 * Android Chrome specific overlay that plays audio to prompt users
 * to adjust their media volume BEFORE we call getUserMedia().
 * 
 * This is a workaround for the Android Chrome bug where getUserMedia()
 * puts the system in MODE_IN_COMMUNICATION, causing volume buttons
 * to control call volume instead of media volume.
 * 
 * By playing audio first, users adjust media volume while it still
 * responds to hardware buttons.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { isAndroidChrome } from '@selna/session';
import { logger } from '../utils/logger';

interface VolumeCheckOverlayProps {
  onContinue: () => void;
  onBack: () => void;
}

// Supported languages for volume check audio
const VOLUME_CHECK_AUDIO_LANGUAGES = ['en-US', 'pt-PT', 'fr-FR', 'es-ES', 'da-DK'] as const;
type VolumeCheckAudioLanguage = typeof VOLUME_CHECK_AUDIO_LANGUAGES[number];

/**
 * Get the audio file path for volume check based on current language.
 * Falls back to English if the current language doesn't have an audio file.
 */
function getVolumeCheckAudioPath(): string {
  const currentLang = i18n.language;
  
  // Check if we have an audio file for the current language
  if (VOLUME_CHECK_AUDIO_LANGUAGES.includes(currentLang as VolumeCheckAudioLanguage)) {
    return `/audio/volume-check-${currentLang}.mp3`;
  }
  
  // Fallback to English
  logger.debug(`[🔊 VOLUME CHECK] No audio for language ${currentLang}, falling back to en-US`);
  return '/audio/volume-check-en-US.mp3';
}

// Re-export isAndroidChrome for backward compatibility
export { isAndroidChrome };

export default function VolumeCheckOverlay({
  onContinue,
  onBack
}: VolumeCheckOverlayProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  
  // Start playing audio when component mounts
  useEffect(() => {
    const audioPath = getVolumeCheckAudioPath();
    logger.debug(`[🔊 VOLUME CHECK] Loading audio: ${audioPath}`);
    const audio = new Audio(audioPath);
    audio.loop = true;
    // Reduce volume significantly so users turn phone volume UP
    // When avatar plays (quieter), volume will be comfortable
    audio.volume = 0.4;
    audioRef.current = audio;
    
    // Handle audio load error (file not found)
    audio.onerror = () => {
      console.warn(`[🔊 VOLUME CHECK] Audio file not found at ${audioPath}`);
      console.warn('[🔊 VOLUME CHECK] Expected files: volume-check-{locale}.mp3 (e.g., volume-check-en-US.mp3)');
      setAudioError(true);
    };
    
    // Try to play
    audio.play()
      .then(() => {
        setIsPlaying(true);
        logger.debug('[🔊 VOLUME CHECK] Audio playing - user can adjust volume');
      })
      .catch((err) => {
        console.error('[🔊 VOLUME CHECK] Failed to play audio:', err);
        setAudioError(true);
      });
    
    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  const handleContinue = () => {
    // Stop audio before continuing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onContinue();
  };
  
  const handleBack = () => {
    // Stop audio before going back
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onBack();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
        {/* Header with speaker icon */}
        <div className="flex items-center justify-center mb-6">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
            isPlaying ? 'bg-green-100 animate-pulse' : audioError ? 'bg-red-100' : 'bg-gray-100'
          }`}>
            <svg 
              className={`w-10 h-10 ${isPlaying ? 'text-green-600' : audioError ? 'text-red-600' : 'text-gray-400'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {isPlaying ? (
                // Speaker with sound waves
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" 
                />
              ) : (
                // Muted speaker
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" 
                />
              )}
            </svg>
          </div>
        </div>
        
        {/* Title */}
        <h3 className="text-xl font-bold text-center mb-4 text-gray-900">
          {t('volumeCheck.title', '🔊 Ajuste o Volume')}
        </h3>
        
        {/* Instructions */}
        <div className="text-center mb-6">
          {isPlaying ? (
            <p className="text-gray-700">
              {t('volumeCheck.instructions', 'Use os botões físicos de volume do seu dispositivo para ajustar até ouvir confortavelmente.')}
            </p>
          ) : audioError ? (
            <>
              <p className="text-amber-600 mb-2">
                {t('volumeCheck.audioErrorAlt', 'Antes de iniciar, por favor verifique se o volume de multimédia do seu dispositivo está num nível adequado.')}
              </p>
              <p className="text-gray-500 text-sm">
                {t('volumeCheck.audioErrorHint', 'Use os botões físicos de volume para ajustar.')}
              </p>
            </>
          ) : (
            <p className="text-gray-500">
              {t('volumeCheck.loading', 'A carregar áudio...')}
            </p>
          )}
        </div>
        
        {/* Visual hint - phone with volume buttons */}
        {isPlaying && (
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* Phone outline */}
              <div className="w-16 h-28 border-2 border-gray-300 rounded-xl bg-gray-50 flex items-center justify-center">
                <div className="w-8 h-16 bg-gray-200 rounded"></div>
              </div>
              {/* Volume buttons indicator - on the RIGHT side of the phone */}
              <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 flex flex-col gap-1">
                <div className="w-2 h-6 bg-green-500 rounded-r animate-bounce"></div>
                <div className="w-2 h-6 bg-green-500 rounded-r animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              </div>
              {/* Arrow pointing to buttons (from far right, pointing left towards the buttons) */}
              <div className="absolute -right-12 top-1/2 transform -translate-y-1/2 text-green-600 animate-pulse text-xl">
                ←
              </div>
            </div>
          </div>
        )}
        
        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            {t('common.buttons.back', 'Voltar')}
          </button>
          <button
            onClick={handleContinue}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            {t('volumeCheck.continue', 'Continuar')} →
          </button>
        </div>
        
        {/* Skip hint for users who already have volume set */}
        <p className="text-xs text-gray-400 text-center mt-4">
          {t('volumeCheck.skipHint', 'Se já ouve bem, pode continuar diretamente.')}
        </p>
        
        {/* Version code for debugging - visible to confirm correct build */}
        <p className="text-xs text-gray-300 text-center mt-2 font-mono">
          v2025-01-10-D
        </p>
      </div>
    </div>
  );
}
